import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  makeStyles
} from '@material-ui/core'
import HelpIcon from '@material-ui/icons/HelpOutline'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react'
import RTE from 'react-rte'
import stripHtml from 'string-strip-html'

import Context from '../../context'
import {
  addOrEditLandingPage,
  checkLandingPagePath,
  getLandingPageDetails,
  getListTags,
  getSearch
} from '../../services/ClubeApi'
import ImageUploadToS3 from '../../services/ImageUpload'
import { getLoggedUser, isAuthenticated } from '../../services/auth'
import history from '../../services/history'

function LandingPagesForm(props) {
  const classes = useStyles()
  const { setUser } = useContext(Context)
  const { landingPageToEdit } = props.location.state || {}
  const [formData, setFormData] = useState({
    title: '',
    description: RTE.createEmptyValue(),
    image: null,
    link: '',
    path: '',
    cta: 'Assine agora!',
    price: '',
    priceDescription: '',
    priceDisclaimer: '',
    partnersTitle: '',
    categoriesTitle: '',
    primaryColor: '#000000',
    primaryColorShade: '#000000',
    secondaryColor: '#000000',
    seoTitle: '',
    seoDescription: '',
    categories: [],
    partners: []
  })
  const [categories, setCategories] = useState([])
  const [loadingInitialData, setLoadingInitialData] = useState(true)
  const [errors, setErrors] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tips, setTips] = useState(null)
  const [mainPartners, setMainPartners] = useReducer(
    (oldState, newState) => ({ ...oldState, ...newState }),
    {
      data: [],
      loading: false,
      chosen: null,
      showOptions: false
    }
  )
  const [othersPartners, setOthersPartners] = useReducer(
    (oldState, newState) => ({ ...oldState, ...newState }),
    {
      data: [],
      loading: false,
      chosen: null,
      showOptions: false
    }
  )

  const Tips = useRef(props => (
    <IconButton size="small" color="primary" onClick={() => setTips(props)}>
      <HelpIcon fontSize="inherit" />
    </IconButton>
  )).current

  const searchPartners = useRef(
    debounce(async (query, fn) => {
      if (query.trim().length < 4) return

      fn({ loading: true })
      const data = await getSearch('partner', query)
      fn({
        data,
        loading: false
      })
    }, 350)
  ).current

  const { setAboutCurrentPage, setShowLoadingModal } = useContext(Context)

  useEffect(() => {
    if (!isAuthenticated) {
      history.replace('/')
      return
    }

    getData()
    setUser(getLoggedUser)
    setAboutCurrentPage({
      title: `${landingPageToEdit ? 'Editar' : 'Nova'} Landing Page`,
      buttons: [{ title: 'Voltar', pathname: '/landing-pages' }]
    })
  }, [])

  async function getData() {
    try {
      setShowLoadingModal(true)
      const [categories, details] = await Promise.all([
        getListTags(),
        landingPageToEdit && getLandingPageDetails(landingPageToEdit.id)
      ])
      setCategories(categories)
      if (details)
        setFormData({
          ...details,
          description: details.description
            ? RTE.createValueFromString(details.description, 'html')
            : RTE.createEmptyValue(),
          categories: details.LandingPagesCategories.map(category => ({
            ...category,
            id: undefined
          })),
          partners: details.LandingPagesPartners.map(partner => ({
            ...partner.Partner,
            ...partner,
            mainList: !!partner.mainList,
            id: undefined
          })),
          LandingPagesCategories: undefined,
          LandingPagesPartners: undefined
        })
    } catch (e) {
      console.log('Error at LandingPagesForm.getData', e)
    } finally {
      setShowLoadingModal(false)
      setLoadingInitialData(false)
    }
  }

  function getPartners(main) {
    return function getPartners(e) {
      searchPartners(e.target.value, main ? setMainPartners : setOthersPartners)
    }
  }

  function getPartnersFn(mainList) {
    return mainList ? setMainPartners : setOthersPartners
  }

  function getPartnersData(mainList) {
    return mainList ? mainPartners : othersPartners
  }

  function handleChange(field) {
    return function handleChange(e) {
      if (loading) return

      let newFormData = { ...formData }
      if (field === 'image') {
        if (e.target.files[0]) {
          newFormData.image = e.target.files[0]
          newFormData.tempImageURL = URL.createObjectURL(e.target.files[0])
        }
      } else if (field === 'description') newFormData.description = e
      else newFormData[field] = e.target.value

      setFormData(newFormData)
    }
  }

  function handleCategoriesChange(index, field) {
    return function handleCategoriesChange(e) {
      if (loading) return

      let newFormData = { ...formData }
      if (!newFormData.categories[index]) newFormData.categories[index] = {}
      if (field === 'image') {
        if (e.target.files[0]) {
          newFormData.categories[index].image = e.target.files[0]
          newFormData.categories[index].tempImageURL = URL.createObjectURL(e.target.files[0])
        }
      } else if (field === 'categoryId') newFormData.categories[index].categoryId = +e.target.value
      else newFormData.categories[index][field] = e.target.value

      setFormData(newFormData)
    }
  }

  function handlePartnersChange(partner, mainList) {
    return function handlePartnersChange() {
      if (loading) return

      let newFormData = { ...formData }
      newFormData.partners.push({
        partnerId: partner.id,
        fantasyName: partner.fantasyName,
        mainList
      })

      setFormData(newFormData)
      getPartnersFn(mainList)({ showOptions: false })
    }
  }

  function handleRemovePartner(partnerId, mainList) {
    return function handleRemovePartner() {
      if (loading) return

      let newFormData = {
        ...formData,
        partners: formData.partners.filter(
          partner => partner.partnerId !== partnerId || partner.mainList !== mainList
        )
      }

      setFormData(newFormData)
    }
  }

  async function checkData() {
    let errors = []
    // image
    if (!formData.image) errors.push('Sem imagem')
    else if (
      formData.image instanceof File &&
      !['image/png', 'image/jpeg', 'image/gif'].includes(formData.image.type)
    )
      errors.push('Imagem em formato indesejável')
    // title
    if (!formData.title.trim()) errors.push('Sem título')
    // description
    if (!stripHtml(formData.description.toString('html')).trim()) errors.push('Sem descrição')
    // link
    if (!formData.link.trim()) errors.push('Sem link')
    // cta
    if (!formData.cta.trim()) errors.push('Sem CTA')
    // price
    if (!formData.price.trim()) errors.push('Sem preço')
    // priceDescription
    if (!formData.priceDescription.trim()) errors.push('Sem descrição de preço')
    // partnersTitle
    if (!formData.partnersTitle.trim()) errors.push('Sem título de parceiros')
    // categoriesTitle
    if (!formData.categoriesTitle.trim()) errors.push('Sem título de categorias')
    // seoTitle
    if (!formData.seoTitle.trim()) errors.push('Sem título de SEO')
    // seoDescription
    if (!formData.seoDescription.trim()) errors.push('Sem descrição de SEO')
    // all colors
    let colorsProps = ['primaryColor', 'primaryColorShade', 'secondaryColor']
    if (colorsProps.some(color => !/^#[0-9a-f]{6}$/i.test(formData[color])))
      errors.push('Cor(es) inválida(s)')
    // categories
    if (formData.categories.length !== 3) errors.push('Categorias insuficientes')
    else if (formData.categories.some(category => !category))
      errors.push('Categorias insuficientes')
    else if (
      formData.categories.some(({ categoryId }) => !categories.find(({ id }) => categoryId === id))
    )
      errors.push('Categoria(s) inválida(s)')
    if (formData.categories.some(category => !category.description.trim()))
      errors.push('Categoria(s) sem descrição')
    if (formData.categories.some(category => !category.image))
      errors.push('Categoria(s) sem imagem')
    else if (
      formData.categories.some(
        category =>
          category.image instanceof File &&
          !['image/png', 'image/jpeg', 'image/gif'].includes(category.image.type)
      )
    )
      errors.push('Imagem de categoria(s) em formato indesejável')
    // main partners
    let mainPartners = formData.partners.filter(partner => partner.mainList)
    if (mainPartners.length < 4) errors.push('Parceiros principais insuficientes')
    // others partners
    let othersPartners = formData.partners.filter(partner => !partner.mainList)
    if (othersPartners.length < 8) errors.push('Outros parceiros insuficientes')
    // path
    if (!formData.path.trim()) errors.push('Sem caminho')
    else if (errors.length === 0) {
      // for avoiding call api if there is any other errors
      let pathIsAvailable = await checkLandingPagePath(formData.path, landingPageToEdit?.id)
      if (!pathIsAvailable) errors.push('Caminho já está em uso')
    }

    return errors.length === 0 ? false : errors
  }

  function hideErrorsModal() {
    setErrors(null)
  }

  function hideTipsModal() {
    setTips(null)
  }

  async function saveAsDraft() {
    execute(3)
  }

  async function update() {
    setShowLoadingModal(true)
    setLoading(true)
    let errors = await checkData()
    setShowLoadingModal(false)
    setLoading(false)
    if (errors) return setErrors(errors)

    execute(1)
  }

  async function execute(situation) {
    if (loading) return

    try {
      setShowLoadingModal(true)
      setLoading(true)
      let finalData = {
        ...formData,
        description: formData.description.toString('html'),
        situation
      }
      if (finalData.image instanceof File) {
        let path = await ImageUploadToS3(finalData.image, 'images/landing-pages/main/')
        finalData.image = process.env.REACT_APP_IMAGES_URL + path
      }
      finalData.categories = await Promise.all(
        finalData.categories.map(async category => {
          if (!(category.image instanceof File)) return category

          let path = await ImageUploadToS3(category.image, 'images/landing-pages/categories/')
          return {
            ...category,
            image: process.env.REACT_APP_IMAGES_URL + path
          }
        })
      )

      await addOrEditLandingPage(finalData)
      setShowLoadingModal(false)
      props.history.push('/landing-pages')
    } catch (e) {
      console.log('Error at LandingPagesForm.execute', e)
    }
  }

  if (loadingInitialData)
    return (
      <div className={classes.outerContainer}>
        <LinearProgress style={{ marginTop: 30 }} />
      </div>
    )

  return (
    <>
      <div className={classes.outerContainer}>
        {loading ? <LinearProgress style={{ marginTop: 30 }} /> : null}
        <div className={classes.content}>
          <div className={classes.main}>
            <div className={classes.twoColumns}>
              <div className={clsx(classes.inputGroup, classes.fullRowTwoColumns)}>
                <span>
                  Título
                  <Tips
                    title="Título"
                    image="/landing-pages-tips/title.png"
                    content={
                      <>
                        Frase destacada em vermelho.
                        <br />
                        Caso queira declarar uma quebra de linha, usar <code>//</code> (duas
                        barras).
                      </>
                    }
                  />
                </span>
                <input value={formData.title} onChange={handleChange('title')} />
              </div>
              <div className={clsx(classes.inputGroup, classes.fullRowTwoColumns)}>
                <span>
                  Descrição
                  <Tips
                    title="Descrição"
                    image="/landing-pages-tips/description.png"
                    content="Frase destacada em vermelho."
                  />
                </span>
                <div style={{ height: 'unset' }}>
                  <RTE
                    className={classes.rte}
                    value={formData.description}
                    onChange={handleChange('description')}
                    toolbarConfig={{
                      display: ['INLINE_STYLE_BUTTONS'],
                      INLINE_STYLE_BUTTONS: [{ label: 'Bold', style: 'BOLD' }]
                    }}
                  />
                </div>
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Link
                  <Tips
                    title="Link"
                    image="/landing-pages-tips/link.png"
                    content="Página que o botão destacado em vermelho deve abrir (e.g. https://clube.gazetadopovo.com.br/assine/)."
                  />
                </span>
                <input value={formData.link} onChange={handleChange('link')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Caminho
                  <Tips
                    title="Caminho"
                    content={
                      <>
                        Qual o caminho que deve acessar a landing page
                        <br />
                        e.g. https://clube.gazetadopovo.com.br/<b>landing-page</b> - em negrito,
                        isto é, sem domínio.
                      </>
                    }
                  />
                </span>
                <input value={formData.path} onChange={handleChange('path')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  CTA
                  <Tips
                    title="CTA"
                    image="/landing-pages-tips/cta.png"
                    content="Frase destacada em vermelho."
                  />
                </span>
                <input value={formData.cta} onChange={handleChange('cta')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Preço
                  <Tips
                    title="Preço"
                    image="/landing-pages-tips/price.png"
                    content="Frase destacada em vermelho (deve incluir todo o necessário, e.g. R$ 9,90/mês)."
                  />
                </span>
                <input value={formData.price} onChange={handleChange('price')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Descrição do preço
                  <Tips
                    title="Descrição do preço"
                    image="/landing-pages-tips/priceDescription.png"
                    content="Frase destacada em vermelho."
                  />
                </span>
                <input
                  value={formData.priceDescription}
                  onChange={handleChange('priceDescription')}
                />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Aviso de preço
                  <Tips
                    title="Aviso de preço"
                    image="/landing-pages-tips/priceDisclaimer.png"
                    content="Frase destacada em vermelho (opcional)."
                  />
                </span>
                <input
                  value={formData.priceDisclaimer}
                  onChange={handleChange('priceDisclaimer')}
                />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Título de parceiros
                  <Tips
                    title="Título de parceiros"
                    image="/landing-pages-tips/partnersTitle.png"
                    content="Frase destacada em vermelho."
                  />
                </span>
                <input value={formData.partnersTitle} onChange={handleChange('partnersTitle')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Título de categorias
                  <Tips
                    title="Título de categorias"
                    image="/landing-pages-tips/categoriesTitle.png"
                    content="Frase destacada em vermelho."
                  />
                </span>
                <input
                  value={formData.categoriesTitle}
                  onChange={handleChange('categoriesTitle')}
                />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Título SEO
                  <Tips
                    title="Título SEO"
                    image="/landing-pages-tips/seoTitle.png"
                    content={
                      <>
                        Frase utilizada como título (<code>{'<title>'}</code>) no HTML para fins de
                        indexação.
                      </>
                    }
                  />
                </span>
                <input value={formData.seoTitle} onChange={handleChange('seoTitle')} />
              </div>
              <div className={classes.inputGroup}>
                <span>
                  Descrição SEO
                  <Tips
                    title="Descrição SEO"
                    image="/landing-pages-tips/seoDescription.png"
                    content={
                      <>
                        Frase utilizada como descrição (<code>{'<meta name="description" />'}</code>
                        ) no HTML para fins de indexação.
                      </>
                    }
                  />
                </span>
                <input value={formData.seoDescription} onChange={handleChange('seoDescription')} />
              </div>
            </div>
            <div className={classes.divider} />
            <div className={classes.threeColumns}>
              <h4 className={clsx(classes.subtitle, classes.fullRowThreeColumns)}>Cores</h4>
              <div className={classes.inputGroup} style={{ color: formData.primaryColor }}>
                <span>
                  Cor primária
                  <Tips
                    title="Cor primária"
                    image="/landing-pages-tips/primaryColor.png"
                    content="Cor destacada em vermelho (verde, no exemplo)."
                  />
                </span>
                <input
                  value={formData.primaryColor}
                  onChange={handleChange('primaryColor')}
                  maxLength={7}
                />
              </div>
              <div className={classes.inputGroup} style={{ color: formData.primaryColorShade }}>
                <span>
                  Cor primária - shade
                  <Tips
                    title="Cor primária - shade"
                    image="/landing-pages-tips/primaryColorShade.png"
                    content="Cor destacada em vermelho (verde escuro, no exemplo)."
                  />
                </span>
                <input
                  value={formData.primaryColorShade}
                  onChange={handleChange('primaryColorShade')}
                  maxLength={7}
                />
              </div>
              <div className={classes.inputGroup} style={{ color: formData.secondaryColor }}>
                <span>
                  Cor secundária
                  <Tips
                    title="Cor secundária"
                    image="/landing-pages-tips/secondaryColor.png"
                    content="Cor destacada em vermelho (rosa, no exemplo)."
                  />
                </span>
                <input
                  value={formData.secondaryColor}
                  onChange={handleChange('secondaryColor')}
                  maxLength={7}
                />
              </div>
            </div>
            <div className={classes.divider} />
            <div className={classes.threeColumns}>
              <h4 className={clsx(classes.subtitle, classes.fullRowThreeColumns)}>
                Categorias
                <Tips
                  title="Categorias"
                  image="/landing-pages-tips/categories.png"
                  content="As três categorias destacadas em vermelho."
                />
              </h4>
              {[0, 1, 2].map((_, i) => (
                <div key={i} className={classes.category}>
                  <div className={classes.inputGroup}>
                    <span>
                      Categoria
                      <Tips
                        title="Categoria"
                        image="/landing-pages-tips/category.png"
                        content="Item destacado em vermelho."
                      />
                    </span>
                    <select
                      value={String(formData.categories[i]?.categoryId || '') || '0'}
                      onChange={handleCategoriesChange(i, 'categoryId')}
                    >
                      <option value="0" disabled />
                      {categories
                        .filter(
                          ({ id }) =>
                            !formData.categories.some(
                              ({ categoryId }, o) => id === categoryId && i !== o
                            )
                        )
                        .map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className={classes.inputGroup}>
                    <span>
                      Descrição
                      <Tips
                        title="Descrição de categoria"
                        image="/landing-pages-tips/categoryDescription.png"
                        content="Frase destacada em vermelho."
                      />
                    </span>
                    <input
                      value={formData.categories[i]?.description}
                      onChange={handleCategoriesChange(i, 'description')}
                    />
                  </div>
                  <div className={classes.inputGroup}>
                    <span>
                      Imagem
                      <Tips
                        title="Imagem de categoria"
                        image="/landing-pages-tips/categoryImage.png"
                        content="Item destacado em vermelho."
                      />
                    </span>
                    <label>
                      {formData.categories[i]?.tempImageURL || formData.categories[i]?.image ? (
                        <img
                          className={classes.img}
                          src={
                            formData.categories[i]?.tempImageURL || formData.categories[i]?.image
                          }
                        />
                      ) : null}
                      <span className={classes.plainButton}>Selecionar arquivo</span>
                      <input
                        className={classes.hiddenInput}
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        onChange={handleCategoriesChange(i, 'image')}
                      />
                    </label>
                    <span className={classes.legend}>Arquivo .png, .jpeg ou .gif.</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={classes.aside}>
            <div className={classes.box}>
              <h4>
                Imagem
                <Tips
                  title="Imagem"
                  image="/landing-pages-tips/image.png"
                  content="Item destacado em vermelho (sem textura)."
                />
              </h4>
              <div>
                <label>
                  {formData.tempImageURL || formData.image ? (
                    <img className={classes.img} src={formData.tempImageURL || formData.image} />
                  ) : null}
                  <span className={classes.plainButton}>Selecionar arquivo</span>
                  <input
                    className={classes.hiddenInput}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    onChange={handleChange('image')}
                  />
                </label>
                <span className={classes.legend}>Arquivo .png, .jpeg ou .gif.</span>
              </div>
            </div>
            {[true, false].map(mainList => (
              <div key={+mainList} className={classes.box}>
                <h4>
                  {mainList ? 'Parceiros principais' : 'Outros parceiros'}
                  <Tips
                    title={mainList ? 'Parceiros principais' : 'Outros parceiros'}
                    image={`/landing-pages-tips/${
                      mainList ? 'mainPartners' : 'othersPartners'
                    }.png`}
                    content="Itens destacados em vermelho."
                  />
                </h4>
                <div>
                  <ul>
                    {formData.partners
                      .filter(partner => partner.mainList === mainList)
                      .map(partner => (
                        <li
                          key={partner.partnerId}
                          onClick={handleRemovePartner(partner.partnerId, mainList)}
                        >
                          {partner.fantasyName}
                        </li>
                      ))}
                    {formData.partners.filter(partner => partner.mainList === mainList).length ===
                      0 && <li disabled>Nenhum selecionado</li>}
                  </ul>
                  <div className={classes.inputGroup}>
                    <input
                      placeholder="Pesquisar"
                      onFocus={() => getPartnersFn(mainList)({ showOptions: true })}
                      onChange={getPartners(mainList)}
                    />
                    {getPartnersData(mainList).showOptions && (
                      <MenuList className={classes.menuList}>
                        {getPartnersData(mainList).loading && (
                          <MenuItem disabled>Pesquisando...</MenuItem>
                        )}
                        {!getPartnersData(mainList).loading &&
                          getPartnersData(mainList).data.length === 0 && (
                            <MenuItem disabled>Nenhum resultado encontrado</MenuItem>
                          )}
                        {getPartnersData(mainList)
                          .data.filter(
                            item =>
                              !formData.partners.some(
                                partner =>
                                  partner.partnerId === item.id && partner.mainList === mainList
                              )
                          )
                          .map(partner => (
                            <MenuItem
                              key={partner.id}
                              onClick={handlePartnersChange(partner, mainList)}
                            >
                              {partner.fantasyName}
                            </MenuItem>
                          ))}
                      </MenuList>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!landingPageToEdit || formData.situation === 3) && (
              <button className={classes.plainButton} type="button" onClick={saveAsDraft}>
                Salvar como rascunho
              </button>
            )}
            <button className={classes.shadowButton} type="button" onClick={update}>
              Publicar
            </button>
          </div>
        </div>
      </div>

      <Dialog
        open={!!errors}
        onClose={hideErrorsModal}
        transitionDuration={{ enter: 250, exit: 0 }}
      >
        <DialogTitle>Verificação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Encontramos os seguintes erros:
            <ul className={classes.ul}>
              {errors?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            Corrija-os antes de prosseguir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={hideErrorsModal}>Ok</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="md"
        scroll="body"
        open={!!tips}
        onClose={hideTipsModal}
        transitionDuration={{ enter: 250, exit: 0 }}
      >
        <DialogTitle>Ajuda - {tips?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">{tips?.content}</DialogContentText>
          <img className={classes.tipsImage} src={tips?.image} />
        </DialogContent>
      </Dialog>
    </>
  )
}

const useStyles = makeStyles({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Montserrat',
    fontWeight: '500'
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: '45px'
  },
  main: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginRight: 30
  },
  twoColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '30px 20px'
  },
  threeColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '30px 20px'
  },
  category: {
    display: 'grid',
    gridTemplateColumns: 'auto',
    gridTemplateRows: 'repeat(2, max-content) auto',
    gap: '30px 0'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '& > span:first-child': {
      display: 'block',
      marginBottom: 10,
      fontSize: '16px',
      fontWeight: 700,
      color: '#30287b'
    },
    '& input, & select': {
      padding: '11px 13px'
    },
    '& input, & select, & > div': {
      height: 44,
      fontSize: '14px',
      color: '#30287b',
      background: 'transparent',
      border: '2px solid #30287b'
    },
    '& select:disabled': {
      display: 'none'
    },
    '&[style]::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: 44,
      height: 44,
      right: 0,
      bottom: 0,
      backgroundColor: 'currentColor',
      zIndex: -1
    }
  },
  fullRowTwoColumns: {
    gridColumn: '1 / 3'
  },
  fullRowThreeColumns: {
    gridColumn: '1 / 4'
  },
  divider: {
    marginTop: '45px',
    borderBottom: '2px solid #b294ff'
  },
  subtitle: {
    marginTop: '45px',
    fontFamily: 'Matrice',
    fontSize: '18px',
    color: '#30287b'
  },
  aside: {
    display: 'grid',
    gridTemplateColumns: 'auto',
    width: 340,
    minWidth: 340,
    gap: '15px'
  },
  box: {
    border: '2px solid #30287b',
    '& h4': {
      marginBottom: 0,
      fontSize: '14px',
      fontWeight: '700',
      color: '#ffffff',
      lineHeight: '44px',
      textAlign: 'center',
      textTransform: 'uppercase',
      background: '#30287b',
      borderBottom: '2px solid #30287b',
      '& button': {
        color: 'inherit'
      }
    },
    '& > div': {
      display: 'grid',
      gridTemplateColumns: 'auto',
      gap: '15px',
      padding: '30px',
      '& > ul': {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        '& li': {
          color: '#30287b',
          cursor: 'pointer',
          '&[disabled]': {
            opacity: 0.5,
            textAlign: 'center',
            cursor: 'default'
          },
          '&:not([disabled])::before': {
            content: '"×"',
            float: 'right',
            fontSize: '18px',
            fontWeight: 700
          },
          '& + li': {
            marginTop: 10
          }
        }
      }
    }
  },
  plainButton: {
    display: 'block',
    width: '100%',
    fontSize: '12px',
    fontWeight: '700',
    color: '#30287b',
    lineHeight: '44px',
    textAlign: 'center',
    textTransform: 'uppercase',
    background: '#ece5fe',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer'
  },
  shadowButton: {
    width: '100%',
    fontSize: '12px',
    fontWeight: '700',
    color: '#30287b',
    lineHeight: '44px',
    textAlign: 'center',
    textTransform: 'uppercase',
    background: '#fbbb33',
    border: 'none',
    borderRadius: '2px',
    boxShadow: '4px 4px #4200f5',
    cursor: 'pointer'
  },
  img: {
    width: '100%',
    height: 'auto',
    backgroundColor: 'grey'
  },
  hiddenInput: {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    width: 0,
    height: 0,
    margin: 0,
    padding: 0
  },
  legend: {
    fontSize: '12px',
    fontStyle: 'italic',
    color: '#30287b',
    textAlign: 'center'
  },
  ul: {
    margin: '15px 0'
  },
  rte: {
    width: '100%',
    minHeight: 70,
    border: 'none'
  },
  menuList: {
    position: 'absolute',
    width: '100%',
    top: '100%',
    left: 0,
    background: '#ece5fe',
    zIndex: 10,
    '& li': {
      fontSize: '12px'
    }
  },
  tipsImage: {
    width: '100%',
    height: 'auto',
    marginBottom: 16
  }
})

export default LandingPagesForm
